import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "../../firebase";
import { C, withAlpha } from "../../constants/theme";
import { toast } from "../../hooks/useToast";
import { fmtMoeda } from "../../utils/format";
import { Btn } from "../ui/Btn";

/* Pagamentos do Mercado Pago para o vendedor conferir: estornado/contestado
   depois de pago, ou não marcado como pago porque algo não confere. Só o
   servidor cria (functions/mercadopago/cobrancas.js — aplicarOrder); aqui o
   dono lê e marca como conferido (as regras só deixam mudar resolvidoEm). */

const MOTIVOS = {
  estornado_apos_pagamento: "O Mercado Pago informou estorno ou contestação depois que o pagamento foi aprovado. Confira antes de enviar o produto e, se for o caso, registre o estorno no pedido.",
  pedido_inexistente: "O pagamento foi confirmado, mas o pedido não existe mais no Print Control. Confira na sua conta Mercado Pago.",
  valor_pago_menor: "O valor pago é menor que o da cobrança, por isso o pedido não foi marcado como pago. Confira na sua conta Mercado Pago.",
  acima_do_saldo: "O pedido já estava pago (por outro meio ou outro Pix/cartão) quando o Mercado Pago confirmou este pagamento, então ele não entrou de novo no pedido nem no caixa. Se foi o mesmo pagamento que você registrou à mão, está tudo certo. Se o cliente pagou duas vezes, devolva o excedente pela sua conta Mercado Pago.",
};
export const textoAlertaMP = alerta => MOTIVOS[alerta?.motivo] ||
  "Os dados deste pagamento não conferem com a cobrança (valor, conta ou moeda), por isso ele não foi marcado como pago. Confira na sua conta Mercado Pago antes de liberar o pedido.";
export const tituloAlertaMP = alerta => (alerta?.tipo === "estornado" ? "Pagamento estornado" : "Pagamento para conferir");

export function useAlertasMercadoPago(uid) {
  const [alertas, setAlertas] = useState([]);
  useEffect(() => {
    if (!uid) { setAlertas([]); return undefined; }
    return onSnapshot(query(collection(db, "users", uid, "mercadopago_alertas"), where("resolvidoEm", "==", null)),
      snap => setAlertas(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.criadoEm || 0) - (a.criadoEm || 0))),
      () => setAlertas([]));
  }, [uid]);
  return alertas;
}

export function AlertasMercadoPago({ uid, style }) {
  const alertas = useAlertasMercadoPago(uid);
  const [salvando, setSalvando] = useState(null);
  if (!alertas.length) return null;

  const conferido = async alerta => {
    setSalvando(alerta.id);
    try { await updateDoc(doc(db, "users", uid, "mercadopago_alertas", alerta.id), { resolvidoEm: serverTimestamp() }); }
    catch { toast("Não foi possível marcar como conferido. Tente de novo.", "error"); }
    finally { setSalvando(null); }
  };

  return (
    <section aria-label="Pagamentos do Mercado Pago para conferir" style={{ background:withAlpha(C.warning, "12"), border:"1px solid "+withAlpha(C.warning, "55"), borderRadius:12, padding:"12px 14px", display:"flex", flexDirection:"column", gap:10, ...style }}>
      <strong style={{ fontSize:13.5, color:C.text }}>⚠️ Pagamentos do Mercado Pago para conferir ({alertas.length})</strong>
      {alertas.map(alerta => (
        <div key={alerta.id} style={{ borderTop:"1px solid "+withAlpha(C.warning, "33"), paddingTop:10, display:"flex", flexDirection:"column", gap:6 }}>
          <div style={{ fontSize:13, color:C.text, fontWeight:700 }}>
            {tituloAlertaMP(alerta)} · {fmtMoeda(alerta.valor)} · {alerta.origem?.tipo === "pedido_online" ? `pedido da loja #${String(alerta.origem.pedidoId || "").slice(-5)}` : "pedido"}
          </div>
          <div style={{ fontSize:12.5, color:C.muted, lineHeight:1.55 }}>{textoAlertaMP(alerta)}</div>
          {alerta.mpOrderId && <div style={{ fontSize:11.5, color:C.muted }}>Order no Mercado Pago: <code>{alerta.mpOrderId}</code></div>}
          <div><Btn small outline onClick={() => conferido(alerta)} disabled={salvando === alerta.id}>{salvando === alerta.id ? "Salvando…" : "Marcar como conferido"}</Btn></div>
        </div>
      ))}
    </section>
  );
}
